import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '@/lib/api';
import { formatIsoBrt } from '@/lib/dateBrt';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './CalendarView.css';
import logger from '@/lib/logger';

const locales = {
  'pt-BR': ptBR,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 0 }),
  getDay,
  locales,
});

const messages = {
  allDay: 'Dia inteiro',
  previous: 'Anterior',
  next: 'Próximo',
  today: 'Hoje',
  month: 'Mês',
  week: 'Semana',
  day: 'Dia',
  agenda: 'Agenda',
  date: 'Data',
  time: 'Hora',
  event: 'Evento',
  noEventsInRange: 'Não há tarefas neste período',
  showMore: (total) => `+ ${total} mais`,
};

const CALENDAR_VIEWS = ['month'];

function TaskCalendar({ user }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // Day-detail modal (opens on day click or on "+ N mais")
  const [dayModalOpen, setDayModalOpen] = useState(false);
  const [dayModalDate, setDayModalDate] = useState(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [projects, setProjects] = useState([]);
  const [projectFilter, setProjectFilter] = useState('all');
  const [clients, setClients] = useState([]);
  const [clientFilter, setClientFilter] = useState('all');
  const [consultants, setConsultants] = useState([]);
  const [consultantFilter, setConsultantFilter] = useState('all');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [daysAhead, setDaysAhead] = useState('all');
  
  // Summary stats
  const [summary, setSummary] = useState(null);
  
  // Edit form
  const [editForm, setEditForm] = useState({
    name: '',
    deadline: '',
    weight: 0,
    status: '',
    client_visible: true,
    notes: '',
    assigned_to: ''
  });

  const fetchProjects = useCallback(async () => {
    try {
      const response = await api.get('/projects');
      setProjects(response.data);
    } catch (error) {
      logger.error('Error fetching projects:', error);
    }
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      const response = await api.get('/clients');
      setClients(response.data);
    } catch (error) {
      logger.error('Error fetching clients:', error);
    }
  }, []);

  const fetchConsultants = useCallback(async () => {
    try {
      const response = await api.get('/users/consultants');
      setConsultants(response.data || []);
    } catch (error) {
      logger.error('Error fetching consultants:', error);
    }
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await api.get('/task-calendar/summary');
      setSummary(response.data);
    } catch (error) {
      logger.error('Error fetching summary:', error);
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status_filter', statusFilter);
      if (projectFilter !== 'all') params.append('project_id', projectFilter);
      if (clientFilter !== 'all') params.append('client_id', clientFilter);
      if (consultantFilter !== 'all') params.append('assigned_to', consultantFilter);
      if (overdueOnly) params.append('overdue_only', 'true');
      if (daysAhead !== 'all') params.append('days_ahead', daysAhead);

      const response = await api.get(`/task-calendar/events?${params}`);

      const formattedEvents = response.data.map((event) => ({
        ...event,
        start: new Date(event.start),
        end: new Date(event.end),
      }));

      setEvents(formattedEvents);
    } catch (error) {
      logger.error('Error fetching task calendar events:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, projectFilter, clientFilter, consultantFilter, overdueOnly, daysAhead]);

  useEffect(() => {
    fetchProjects();
    fetchClients();
    fetchConsultants();
    fetchSummary();
  }, [fetchProjects, fetchClients, fetchConsultants, fetchSummary]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setDialogOpen(true);
  };

  const openEditDialog = () => {
    if (!selectedEvent || selectedEvent.resource.type === 'protocol') return;
    setEditForm({
      name: selectedEvent.resource.task_name,
      deadline: selectedEvent.resource.deadline,
      weight: selectedEvent.resource.weight,
      status: selectedEvent.resource.status,
      client_visible: selectedEvent.resource.client_visible,
      notes: selectedEvent.resource.notes || '',
      assigned_to: selectedEvent.resource.assigned_to || ''
    });
    setDialogOpen(false);
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!selectedEvent) return;
    
    try {
      await api.put(`/tasks/${selectedEvent.resource.task_id}`, editForm);
      toast.success('Tarefa atualizada com sucesso!');
      setEditDialogOpen(false);
      fetchEvents();
      fetchSummary();
    } catch (error) {
      logger.error('Error updating task:', error);
      toast.error('Erro ao atualizar tarefa');
    }
  };

  const handleCancelTask = async () => {
    if (!selectedEvent) return;
    
    if (!window.confirm('Tem certeza que deseja cancelar esta tarefa?')) return;
    
    try {
      await api.put(`/tasks/${selectedEvent.resource.task_id}/cancel`);
      toast.success('Tarefa cancelada com sucesso!');
      setDialogOpen(false);
      fetchEvents();
      fetchSummary();
    } catch (error) {
      logger.error('Error cancelling task:', error);
      toast.error('Erro ao cancelar tarefa');
    }
  };

  const eventStyleGetter = (event) => {
    const isProtocol = event.resource?.type === 'protocol';
    
    const style = {
      backgroundColor: event.resource.color,
      borderRadius: '4px',
      opacity: 0.9,
      color: 'white',
      border: isProtocol ? '2px dashed rgba(255,255,255,0.5)' : '0px',
      display: 'block',
      fontSize: '12px',
      padding: '2px 5px',
      fontWeight: isProtocol ? '600' : '400',
    };
    return { style };
  };

  const getStatusBadge = (status) => {
    const styles = {
      not_started: 'bg-gray-100 text-gray-700',
      in_progress: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700',
    };
    const labels = {
      not_started: 'Não Iniciada',
      in_progress: 'Em Andamento',
      completed: 'Concluída',
      cancelled: 'Cancelada',
    };
    return (
      <Badge className={styles[status] || 'bg-gray-100 text-gray-700'}>
        {labels[status] || status}
      </Badge>
    );
  };

  const canEdit = ['ADMIN', 'CONSULTANT'].includes(user?.role?.toUpperCase());

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-800"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-gray-900">Calendário de Atividades</h1>
            <p className="text-gray-600 mt-1">Acompanhe os prazos das tarefas dos projetos</p>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="p-4 border border-red-200 bg-red-50">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-700 font-medium">Atrasadas</p>
                  <p className="text-2xl font-bold text-red-900 mt-1">{summary.overdue}</p>
                </div>
                <div className="w-10 h-10 bg-red-200 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </Card>

            <Card className="p-4 border border-orange-200 bg-orange-50">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-700 font-medium">Próx. 7 dias</p>
                  <p className="text-2xl font-bold text-orange-900 mt-1">{summary.due_in_7_days}</p>
                </div>
                <div className="w-10 h-10 bg-orange-200 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-orange-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </Card>

            <Card className="p-4 border border-yellow-200 bg-yellow-50">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-700 font-medium">Próx. 14 dias</p>
                  <p className="text-2xl font-bold text-yellow-900 mt-1">{summary.due_in_14_days}</p>
                </div>
                <div className="w-10 h-10 bg-yellow-200 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-yellow-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
            </Card>

            <Card className="p-4 border border-blue-200 bg-blue-50">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-700 font-medium">Próx. 30 dias</p>
                  <p className="text-2xl font-bold text-blue-900 mt-1">{summary.due_in_30_days}</p>
                </div>
                <div className="w-10 h-10 bg-blue-200 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="p-4 border border-gray-200">
          <div className="flex flex-wrap gap-3 items-center">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos Status</SelectItem>
                <SelectItem value="not_started">Não Iniciada</SelectItem>
                <SelectItem value="in_progress">Em Andamento</SelectItem>
                <SelectItem value="completed">Concluída</SelectItem>
              </SelectContent>
            </Select>

            {canEdit && (
              <>
                <Select value={projectFilter} onValueChange={setProjectFilter}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Projeto" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos Projetos</SelectItem>
                    {projects.map((project) => (
                      <SelectItem key={project.project_id} value={project.project_id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={clientFilter} onValueChange={setClientFilter}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos Clientes</SelectItem>
                    {clients.map((client) => (
                      <SelectItem key={client.user_id} value={client.user_id}>
                        {client.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={consultantFilter} onValueChange={setConsultantFilter}>
                  <SelectTrigger className="w-48" data-testid="consultant-filter">
                    <SelectValue placeholder="Consultor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos Consultores</SelectItem>
                    {consultants.map((c) => (
                      <SelectItem key={c.user_id} value={c.full_name}>
                        {c.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            )}

            <Select value={daysAhead} onValueChange={setDaysAhead}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Período" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todo Período</SelectItem>
                <SelectItem value="7">Próx. 7 dias</SelectItem>
                <SelectItem value="14">Próx. 14 dias</SelectItem>
                <SelectItem value="30">Próx. 30 dias</SelectItem>
              </SelectContent>
            </Select>

            <Button
              variant={overdueOnly ? "default" : "outline"}
              onClick={() => setOverdueOnly(!overdueOnly)}
              className={overdueOnly ? "bg-red-600 hover:bg-red-700" : ""}
            >
              Apenas Atrasadas
            </Button>
          </div>
        </Card>

        {/* Calendar */}
        <Card className="p-6 border border-gray-200 overflow-visible">
          <div className="min-h-[600px]">
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              style={{ height: 600 }}
              onSelectEvent={handleSelectEvent}
              onShowMore={(_events, date) => {
                setDayModalDate(date);
                setDayModalOpen(true);
              }}
              onDrillDown={(date) => {
                setDayModalDate(date);
                setDayModalOpen(true);
              }}
              eventPropGetter={eventStyleGetter}
              messages={messages}
              culture="pt-BR"
              views={CALENDAR_VIEWS}
              view={view}
              onView={setView}
              date={currentDate}
              onNavigate={setCurrentDate}
              popup={false}
            />
          </div>
        </Card>

        {/* Task/Protocol Details Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {selectedEvent?.resource?.type === 'protocol' 
                  ? 'Detalhes do Protocolo' 
                  : 'Detalhes da Tarefa'
                }
              </DialogTitle>
            </DialogHeader>
            {selectedEvent && selectedEvent.resource?.type === 'protocol' ? (
              // Protocol Details
              <div className="space-y-4">
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-200 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-purple-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-purple-600 font-medium">Data de Protocolo</p>
                      <p className="text-lg font-bold text-purple-900">{selectedEvent.resource.phase_name}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Projeto</p>
                    <p className="font-semibold text-gray-900">{selectedEvent.resource.project_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Cliente</p>
                    <p className="font-medium text-gray-900">{selectedEvent.resource.client_name}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Data do Protocolo</p>
                    <p className="font-medium text-gray-900">
                      {new Date(selectedEvent.start).toLocaleDateString('pt-BR', {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    <p className={`font-bold text-lg ${
                      selectedEvent.resource.days_until < 0
                        ? 'text-red-600'
                        : selectedEvent.resource.days_until <= 3
                        ? 'text-orange-600'
                        : selectedEvent.resource.days_until <= 7
                        ? 'text-yellow-600'
                        : 'text-green-600'
                    }`}>
                      {selectedEvent.resource.days_until < 0
                        ? `Passou há ${Math.abs(selectedEvent.resource.days_until)} dias`
                        : selectedEvent.resource.days_until === 0
                        ? 'Hoje!'
                        : `Em ${selectedEvent.resource.days_until} dias`}
                    </p>
                  </div>
                </div>

                <div className="flex justify-between pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => window.open(`/projects/${selectedEvent.resource.project_id}`, '_blank')}
                  >
                    Ver Projeto
                  </Button>
                  <Button variant="outline" onClick={() => setDialogOpen(false)}>
                    Fechar
                  </Button>
                </div>
              </div>
            ) : selectedEvent && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Tarefa</p>
                    <p className="font-semibold text-gray-900">{selectedEvent.resource.task_name || selectedEvent.title}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    <div className="mt-1">{getStatusBadge(selectedEvent.resource.status)}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Projeto</p>
                    <p className="font-medium text-gray-900">{selectedEvent.resource.project_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Cliente</p>
                    <p className="font-medium text-gray-900">{selectedEvent.resource.client_name}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Prazo</p>
                    <p className="font-medium text-gray-900">
                      {new Date(selectedEvent.start).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Dias até o Prazo</p>
                    <p className={`font-bold text-lg ${
                      selectedEvent.resource.days_until_deadline < 0
                        ? 'text-red-600'
                        : selectedEvent.resource.days_until_deadline <= 3
                        ? 'text-orange-600'
                        : selectedEvent.resource.days_until_deadline <= 7
                        ? 'text-yellow-600'
                        : 'text-green-600'
                    }`}>
                      {selectedEvent.resource.days_until_deadline < 0
                        ? `Atrasada ${Math.abs(selectedEvent.resource.days_until_deadline)} dias`
                        : `${selectedEvent.resource.days_until_deadline} dias`}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Responsável</p>
                    <p className="font-medium text-gray-900">
                      {selectedEvent.resource.assigned_to || 'Não atribuído'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Peso</p>
                    <p className="font-medium text-gray-900">{selectedEvent.resource.weight}%</p>
                  </div>
                </div>

                {selectedEvent.resource.notes && (
                  <div>
                    <p className="text-sm text-gray-500">Observações</p>
                    <p className="mt-1 text-gray-900">{selectedEvent.resource.notes}</p>
                  </div>
                )}

                <div className="flex justify-between pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => window.open(`/projects/${selectedEvent.resource.project_id}`, '_blank')}
                  >
                    Ver Projeto
                  </Button>
                  
                  <div className="flex space-x-3">
                    {canEdit && selectedEvent.resource.status !== 'cancelled' && (
                      <>
                        <Button
                          variant="outline"
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          onClick={handleCancelTask}
                        >
                          Cancelar Tarefa
                        </Button>
                        <Button
                          onClick={openEditDialog}
                          className="bg-emerald-800 hover:bg-emerald-900"
                        >
                          Editar Tarefa
                        </Button>
                      </>
                    )}
                    <Button variant="outline" onClick={() => setDialogOpen(false)}>
                      Fechar
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Task Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Editar Tarefa</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <Label htmlFor="task_name">Nome da Tarefa</Label>
                <Input
                  id="task_name"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="deadline">Prazo</Label>
                  <Input
                    id="deadline"
                    type="date"
                    value={editForm.deadline}
                    onChange={(e) => setEditForm({ ...editForm, deadline: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="weight">Peso (%)</Label>
                  <Input
                    id="weight"
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={editForm.weight}
                    onChange={(e) => setEditForm({ ...editForm, weight: parseFloat(e.target.value) })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={editForm.status}
                    onValueChange={(value) => setEditForm({ ...editForm, status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_started">Não Iniciada</SelectItem>
                      <SelectItem value="in_progress">Em Andamento</SelectItem>
                      <SelectItem value="completed">Concluída</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="assigned_to">Responsável</Label>
                  <Input
                    id="assigned_to"
                    value={editForm.assigned_to}
                    onChange={(e) => setEditForm({ ...editForm, assigned_to: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="notes">Observações</Label>
                <Textarea
                  id="notes"
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="client_visible"
                  checked={editForm.client_visible}
                  onChange={(e) => setEditForm({ ...editForm, client_visible: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-emerald-800 focus:ring-emerald-800"
                />
                <Label htmlFor="client_visible" className="text-sm font-normal cursor-pointer">
                  Visível para o cliente
                </Label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-emerald-800 hover:bg-emerald-900">
                  Salvar Alterações
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Day Detail Modal - shows ALL activities of the clicked day */}
        <Dialog open={dayModalOpen} onOpenChange={setDayModalOpen}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>
                {dayModalDate && new Date(dayModalDate).toLocaleDateString('pt-BR', {
                  weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
                })}
              </DialogTitle>
              <DialogDescription>
                Todas as atividades cadastradas neste dia
              </DialogDescription>
            </DialogHeader>
            <div className="overflow-y-auto flex-1 -mx-2 px-2">
              {(() => {
                if (!dayModalDate) return null;
                const dayStr = formatIsoBrt(dayModalDate);
                const dayEvents = events.filter((ev) => {
                  const d = new Date(ev.start);
                  return formatIsoBrt(d) === dayStr;
                });
                if (dayEvents.length === 0) {
                  return <p className="text-center text-gray-500 py-8">Nenhuma atividade neste dia.</p>;
                }
                return (
                  <ul className="divide-y divide-gray-100">
                    {dayEvents.map((ev) => {
                      const r = ev.resource || {};
                      const isProtocol = r.type === 'protocol';
                      return (
                        <li
                          key={ev.id || `${r.task_id || r.phase_id}-${ev.start}`}
                          onClick={() => {
                            setSelectedEvent(ev);
                            setDayModalOpen(false);
                            setDialogOpen(true);
                          }}
                          className="py-3 px-2 hover:bg-gray-50 rounded cursor-pointer flex items-start gap-3"
                          data-testid="day-modal-activity"
                        >
                          <div className={`w-2 h-2 rounded-full mt-2 shrink-0 ${isProtocol ? 'bg-purple-500' : r.status === 'completed' ? 'bg-green-500' : r.is_overdue ? 'bg-red-500' : 'bg-blue-500'}`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">{ev.title}</p>
                            <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500 mt-0.5">
                              {r.project_name && <span>📁 {r.project_name}</span>}
                              {r.assigned_to && <span>👤 {r.assigned_to}</span>}
                              {isProtocol && <span className="text-purple-600">Protocolo</span>}
                              {r.is_overdue && <span className="text-red-600 font-medium">Atrasada</span>}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                );
              })()}
            </div>
            <div className="pt-3 border-t border-gray-100 text-xs text-gray-500">
              Clique em uma atividade para abrir os detalhes
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

export default TaskCalendar;
